import React, { useState } from "react";
import Box from "@mui/material/Box";
import Card from "@mui/material/Card";
import Chip from "@mui/material/Chip";
import CircularProgress from "@mui/material/CircularProgress";
import List from "@mui/material/List";
import ListItem from "@mui/material/ListItem";
import ListItemIcon from "@mui/material/ListItemIcon";
import ListItemText from "@mui/material/ListItemText";
import {
  updateContactNumber,
  updateFirstName,
  updateLastName,
  updatePreferredName,
  updatePronouns,
} from "../users/users-service";
import { UpdatableNameTextField } from "./UpdatableNameTextField";
import { ThisUser } from "../games/query-keys";
import { UserAvatarCardCover } from "../users/UserAvatar";
import Check from "@mui/icons-material/Check";
import { useMyProfile, useProfileStatistics, useUpdateProfile, useVerifyEmail } from "@seabrookstudios/pitch2table";
import { enqueueSnackbar } from "notistack";
import { EditableSwitch } from "../core/EditableSwitch";
import { EditableListItemUrl } from "../core/EditableListItemUrl";
import { shortProfileUrl } from "../games/model";
import Button from "@mui/material/Button";
import CheckCircle from "@mui/icons-material/CheckCircle";
import Stack from "@mui/material/Stack";
import { getStatAmount, makeProfileEmailRequested, makeProfileViewed } from "@seabrookstudios/pitch2table-core";
import { IsMember, LockedListItem } from "../core/HasEntitlements";

const FirstNameTextField = ({ initialValue }) => {
  return (
    <UpdatableNameTextField
      label="First Name"
      helperText="Your first name, as you'd like it to appear on the box"
      initialValue={initialValue}
      updateFunction={updateFirstName}
      queryKey={ThisUser()}
    />
  );
};

const LastNameTextField = ({ initialValue }) => {
  return (
    <UpdatableNameTextField
      label="Last Name"
      helperText="Your last name, as you'd like it to appear on the box"
      initialValue={initialValue}
      updateFunction={updateLastName}
      queryKey={ThisUser()}
    />
  );
};

const PreferredNameTextField = ({ initialValue }) => {
  return (
    <UpdatableNameTextField
      label="Preferred Name"
      helperText="The name you'd like people to call you."
      initialValue={initialValue}
      updateFunction={updatePreferredName}
      queryKey={ThisUser()}
    />
  );
};

const PronounsTextField = ({ initialValue }) => {
  return (
    <UpdatableNameTextField
      label="Pronouns"
      helperText="What are your pronouns?"
      initialValue={initialValue}
      updateFunction={updatePronouns}
      queryKey={ThisUser()}
    />
  );
};

const ContactNumberTextField = ({ initialValue }) => {
  return (
    <UpdatableNameTextField
      label="Contact Number"
      helperText="Your preferred contact number (mobile or landline)"
      initialValue={initialValue}
      updateFunction={updateContactNumber}
      queryKey={ThisUser()}
    />
  );
};

/**
 *
 * @param {object} props
 * @param {string|undefined} props.initialValue
 * @param {boolean} props.disabled
 * @returns
 */
const SlugTextField = ({ initialValue, disabled }) => {
  const onSuccess = () => {
    enqueueSnackbar(`Username updated.`, { variant: "success" });
  };

  const onFailure = () => {
    enqueueSnackbar(`Username update failed. Name taken`, { variant: "error" });
  };

  const updateProfile = useUpdateProfile(onSuccess, onFailure);

  /**
   * @param {string} slug
   * @returns {Promise<>}
   */
  const updateSlug = async (slug) => {
    await updateProfile.mutateAsync({
      toUpdate: {
        slug,
        firstName: undefined,
        preferredCurrency: undefined,
        city: undefined,
        contactNumber: undefined,
        country: undefined,
        lastName: undefined,
        marketingConsent: undefined,
        preferredName: undefined,
        pronouns: undefined,
        sendDailyEmails: undefined,
        sendNotifications: undefined,
        timezone: undefined,
        whenToSendDailyEmails: undefined,
        isPublic: undefined,
        showEmail: undefined,
        travel: undefined,
        isTravelling: undefined,
      },
    });
  };

  return (
    <EditableListItemUrl
      title="Username"
      text={initialValue}
      onSave={updateSlug}
      disabled={disabled}
      noIcon={true}
      formatter={(text) => {
        if (disabled) {
          return "Verify your email to reserve a username.";
        }

        return !text ? "" : shortProfileUrl(text);
      }}
      multiline={false}
      isBusy={updateProfile.isPending}
      helperText="Changing your username will break existing links to your profile or games that you have shared."
    />
  );
};

/**
 * @param {object} props
 * @param {string} props.slug
 */
const TotalProfileViews = ({ slug }) => {
  const { data: stats = [] } = useProfileStatistics(slug);

  return (
    <ListItem>
      <ListItemText primary="Total Profile Views" secondary={getStatAmount(stats, makeProfileViewed(slug), 0)} />
    </ListItem>
  );
};

/**
 * @param {object} props
 * @param {string} props.slug
 */
const TotalEmailViews = ({ slug }) => {
  const { data: stats = [] } = useProfileStatistics(slug);

  return (
    <ListItem>
      <ListItemText primary="Total Email Views" secondary={getStatAmount(stats, makeProfileEmailRequested(slug), 0)} />
    </ListItem>
  );
};

/**
 * @param {object} props
 * @param {boolean} props.initialValue
 * @param {string} props.slug
 */
const PublicAccountToggle = ({ initialValue, slug }) => {
  const onSuccess = () => {
    enqueueSnackbar(`Visibility changed.`, { variant: "success" });
  };

  const onFailure = () => {
    enqueueSnackbar(`Visibility update failed.`, { variant: "error" });
  };

  const updateProfile = useUpdateProfile(onSuccess, onFailure);

  /**
   * @param {boolean} isPublic
   * @returns {Promise<>}
   */
  const updateVisibility = async (isPublic) => {
    await updateProfile.mutateAsync({
      toUpdate: {
        slug: undefined,
        firstName: undefined,
        preferredCurrency: undefined,
        city: undefined,
        contactNumber: undefined,
        country: undefined,
        lastName: undefined,
        marketingConsent: undefined,
        preferredName: undefined,
        pronouns: undefined,
        sendDailyEmails: undefined,
        sendNotifications: undefined,
        timezone: undefined,
        whenToSendDailyEmails: undefined,
        showEmail: undefined,
        isPublic,
        travel: undefined,
        isTravelling: undefined,
      },
    });
  };

  return (
    <Stack direction="row" justifyContent="space-between" flex={1}>
      <Box flex={1}>
        <EditableSwitch
          initialValue={initialValue}
          onUpdate={updateVisibility}
          title="Profile Visibility"
          onLabel="PUBLIC - People with a link to your profile can view it."
          offLabel="PRIVATE - No one can view your profile except co-designers."
        />
      </Box>
      <IsMember
        LockedView={
          <Box flex={1}>
            <LockedListItem title="Total Profile Views" />
          </Box>
        }
      >
        <Box flex={1}>
          <TotalProfileViews slug={slug} />
        </Box>
      </IsMember>
    </Stack>
  );
};

/**
 * @param {object} props
 * @param {boolean} props.initialValue
 * @param {string} props.slug
 */
const ShowEmailToggle = ({ initialValue, slug }) => {
  const onSuccess = () => {
    enqueueSnackbar(`Show email changed.`, { variant: "success" });
  };

  const onFailure = () => {
    enqueueSnackbar(`Show email update failed.`, { variant: "error" });
  };

  const updateProfile = useUpdateProfile(onSuccess, onFailure);

  /**
   * @param {boolean} showEmail
   * @returns {Promise<>}
   */
  const updateVisibility = async (showEmail) => {
    await updateProfile.mutateAsync({
      toUpdate: {
        slug: undefined,
        firstName: undefined,
        preferredCurrency: undefined,
        city: undefined,
        contactNumber: undefined,
        country: undefined,
        lastName: undefined,
        marketingConsent: undefined,
        preferredName: undefined,
        pronouns: undefined,
        sendDailyEmails: undefined,
        sendNotifications: undefined,
        timezone: undefined,
        whenToSendDailyEmails: undefined,
        isPublic: undefined,
        showEmail,
        travel: undefined,
        isTravelling: undefined,
      },
    });
  };

  return (
    <Stack direction="row" justifyContent="space-between" flex={1}>
      <Box flex={1}>
        <EditableSwitch
          initialValue={initialValue}
          onUpdate={updateVisibility}
          title="Show Email"
          onLabel="VISIBLE - Users with access to your profile page will able to see your email."
          offLabel="PRIVATE - No one can view your email address."
        />
      </Box>
      <IsMember
        LockedView={
          <Box flex={1}>
            <LockedListItem title="Total Email Views" />
          </Box>
        }
      >
        <Box flex={1}>
          <TotalEmailViews slug={slug} />
        </Box>
      </IsMember>
    </Stack>
  );
};

const VerifyEmailButton = () => {
  const [clicked, setClicked] = useState(false);
  const verifyEmail = useVerifyEmail();

  const onVerifyHandler = async () => {
    await verifyEmail.mutateAsync();
    setClicked(true);
  };

  return <Button onClick={onVerifyHandler}>{clicked ? <CheckCircle /> : "VERIFY EMAIL"}</Button>;
};

export const AboutYou = () => {
  const { data: contact, isLoading } = useMyProfile();

  if (isLoading) {
    return (
      <Card elevation={1} square={false}>
        <Box p={2} justifyContent="center">
          <CircularProgress />
        </Box>
      </Card>
    );
  }

  if (!contact) {
    return (
      <Card elevation={1} square={false}>
        <Box p={2} justifyContent="center">
          <span>An error occurred, please refresh the page.</span>
        </Box>
      </Card>
    );
  }

  return (
    <Card elevation={1} square={false}>
      <List>
        <Box p={1}>
          <UserAvatarCardCover editable />
        </Box>
        <ListItem>
          <FirstNameTextField initialValue={contact.firstName} />
        </ListItem>
        <ListItem>
          <LastNameTextField initialValue={contact.lastName} />
        </ListItem>
        <ListItem>
          <PreferredNameTextField initialValue={contact.preferredName} />
        </ListItem>
        <ListItem>
          <PronounsTextField initialValue={contact.pronouns} />
        </ListItem>
        <ListItem>
          <ListItemText primary="Email" secondary={contact.emailSet ? contact.email : "Pending"} />
          {contact.emailVerified ? (
            <ListItemIcon>
              <Box justifyContent="center" alignItems="center" display="flex" flex={1}>
                <Chip icon={<Check />} label="VERIFIED" color="success" />
              </Box>
            </ListItemIcon>
          ) : (
            <ListItemIcon>
              <Box justifyContent="center" alignItems="center" display="flex" flex={1}>
                <VerifyEmailButton />
              </Box>
            </ListItemIcon>
          )}
        </ListItem>
        <ListItem>
          <ContactNumberTextField initialValue={contact.contactNumber} />
        </ListItem>
        <SlugTextField initialValue={contact.slug} disabled={!contact.emailVerified} />
        {contact.slug && (
          <ListItem>
            <PublicAccountToggle initialValue={contact.isPublic} slug={contact.slug} />
          </ListItem>
        )}
        {contact.slug && (
          <ListItem>
            <ShowEmailToggle initialValue={contact.showEmail} slug={contact.slug} />
          </ListItem>
        )}
      </List>
    </Card>
  );
};
